import { useCallback, useEffect, useRef, useState } from "react";
import { useLatestRequest } from "../../../hooks/useLatestRequest";
import { getApiErrorMessage } from "../../../lib/result-codes";
import { getRecentActivityList } from "../api/recentActivity.api";
import type { RecentActivityItem } from "../types/recentActivity.types";

const PAGE_SIZE = 10;

export interface UseGetRecentActivityListReturn {
  activities: RecentActivityItem[];
  loading: boolean;
  error: string | null;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  totalCount: number;
  refetch: () => Promise<void>;
}

export const useGetRecentActivityList = (): UseGetRecentActivityListReturn => {
  const [activities, setActivities] = useState<RecentActivityItem[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const { run, loading } = useLatestRequest();
  const requestIdRef = useRef(0);
  const index = page - 1;

  const fetchActivities = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setError(null);
    try {
      const response = await run((signal) => getRecentActivityList(index, PAGE_SIZE, { signal }));
      if (!response || requestId !== requestIdRef.current) return;
      setActivities(response.list);
      setTotalCount(response.paging.total);
    } catch (requestError: unknown) {
      if (requestId !== requestIdRef.current) return;
      setActivities([]);
      setTotalCount(0);
      setError(getApiErrorMessage(requestError, "Failed to fetch recent activities"));
    }
  }, [index, run]);

  useEffect(() => {
    void fetchActivities();
    return () => {
      requestIdRef.current += 1;
    };
  }, [fetchActivities]);

  return { activities, loading, error, page, setPage, totalCount, refetch: fetchActivities };
};
